import type { PayexThunkAction } from 'store/configureStore';

import { newNotification } from 'actions/ui/ui';
import {
  CANCEL_RECURRING_RECEIVABLE_FAILURE,
  CANCEL_RECURRING_RECEIVABLE_REQUEST,
  CANCEL_RECURRING_RECEIVABLE_SUCCESS,
  CREATE_RECURRING_RECEIVABLE_FAILURE,
  CREATE_RECURRING_RECEIVABLE_REQUEST,
  CREATE_RECURRING_RECEIVABLE_SUCCESS,
  FETCH_USER_RECURRING_RECEIVABLES_FAILURE,
  FETCH_USER_RECURRING_RECEIVABLES_REQUEST,
  FETCH_USER_RECURRING_RECEIVABLES_SILENTLY_FAILURE,
  FETCH_USER_RECURRING_RECEIVABLES_SILENTLY_REQUEST,
  FETCH_USER_RECURRING_RECEIVABLES_SILENTLY_SUCCESS,
  FETCH_USER_RECURRING_RECEIVABLES_SUCCESS,
  IS_FETCHING_RECURRING_RECEIVABLE_CANCELLATION_FLAG,
  IS_FETCHING_USER_RECURRING_RECEIVABLES_FLAG,
  PERMANENT_COOKIE_NAME,
} from 'constants/index';
import {
  getCurrentLocale,
  getNumberOfInstallments,
  getOffersRecurringConfigurationId,
  getOrder,
  getSelectedRecurringOffer,
  hasGatewayOrigin,
  isFetching,
} from 'selectors';
import { cookies } from 'utils/cookies/cookies';

import type { TokenizationParams } from 'domain/recurringReceivables/repository/types';

type APIError = {
  parsedBody?: { errors?: { type: string; message: string }[] };
  status: number;
};

export const fetchUserRecurringReceivables =
  (params = {}, silently = false): PayexThunkAction =>
  async (dispatch, getState, { recurringReceivables }) => {
    try {
      const state = getState();
      const isFetchingRecurringReceivables = isFetching(
        state,
        IS_FETCHING_USER_RECURRING_RECEIVABLES_FLAG,
      );

      if (isFetchingRecurringReceivables) {
        return null;
      }

      dispatch({
        type: silently
          ? FETCH_USER_RECURRING_RECEIVABLES_SILENTLY_REQUEST
          : FETCH_USER_RECURRING_RECEIVABLES_REQUEST,
      });

      const response = await recurringReceivables.fetch(params);

      return dispatch({
        type: silently
          ? FETCH_USER_RECURRING_RECEIVABLES_SILENTLY_SUCCESS
          : FETCH_USER_RECURRING_RECEIVABLES_SUCCESS,
        payload: response,
      });
    } catch (error) {
      return dispatch({
        type: silently
          ? FETCH_USER_RECURRING_RECEIVABLES_SILENTLY_FAILURE
          : FETCH_USER_RECURRING_RECEIVABLES_FAILURE,
        payload: error,
      });
    }
  };

export const fetchUserRecurringReceivablesIfNeeded =
  (params = {}): PayexThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const {
      entities: { recurringReceivables = [] } = {},
    } = getState();

    if (recurringReceivables.length > 0) {
      return Promise.resolve();
    }

    return dispatch(fetchUserRecurringReceivables(params));
  };

export const cancelRecurringReceivable =
  (id: string): PayexThunkAction =>
  async (dispatch, getState, { recurringReceivables }) => {
    try {
      const state = getState();
      const isCancellingRecurringReceivable = isFetching(
        state,
        IS_FETCHING_RECURRING_RECEIVABLE_CANCELLATION_FLAG,
      );

      if (isCancellingRecurringReceivable) {
        return;
      }

      const locale = getCurrentLocale(state);

      dispatch({ type: CANCEL_RECURRING_RECEIVABLE_REQUEST });

      await recurringReceivables.cancel(id, locale);

      return dispatch({
        type: CANCEL_RECURRING_RECEIVABLE_SUCCESS,
        payload: { id },
      });
    } catch (error) {
      const message =
        (error as APIError)?.parsedBody?.errors?.[0]?.message ||
        'notifications.cancel_recurring_receivable_failure';
      const noti = newNotification({ message, type: 'error' });
      dispatch(noti);
      return dispatch({
        type: CANCEL_RECURRING_RECEIVABLE_FAILURE,
      });
    }
  };

export const createRecurringReceivable =
  (tokenizationParams?: {
    apiConfirmationURL: string;
    sessionId: string;
  }): PayexThunkAction<Promise<{ type: string }>> =>
  async (dispatch, getState, { recurringReceivables }) => {
    try {
      const state = getState();
      const fingerprint = cookies.read(PERMANENT_COOKIE_NAME);
      const locale = getCurrentLocale(state);
      const { id: orderId, token: orderToken } = getOrder(state);
      const numberOfInstallments = getNumberOfInstallments(state);
      const isNonAutomated = !getSelectedRecurringOffer(state).recurring;
      const isFromGateway = hasGatewayOrigin(state);
      const recurringConfigurationId = getOffersRecurringConfigurationId(state);

      dispatch({ type: CREATE_RECURRING_RECEIVABLE_REQUEST });

      await recurringReceivables.create({
        ...(tokenizationParams || {}),
        fingerprint,
        locale,
        numberOfInstallments,
        orderId,
        orderToken,
        nonAutomated: isNonAutomated,
        isFromGateway,
        configurationId: recurringConfigurationId,
      });

      return dispatch({ type: CREATE_RECURRING_RECEIVABLE_SUCCESS });
    } catch (error) {
      dispatch({
        type: CREATE_RECURRING_RECEIVABLE_FAILURE,
        payload: error,
      });

      throw error;
    }
  };
